// hooks/useMuseums.js - Custom hook for museum CRUD operations
import { useState, useCallback, useEffect } from 'react';
import {
  createMuseum,
  updateMuseum,
  getMuseum,
  getAllMuseums,
  deleteMuseum,
  addRoom,
  removeRoom,
  updateRooms,
  addCategory,
  removeCategory,
  updateCategories,
  linkGuide,
  unlinkGuide,
  updateLinkedGuides,
  getMuseumByGuideId
} from '../MuseumObjects/utils/museumsDatabase';

export const useMuseums = () => {
  const [museums, setMuseums] = useState([]);
  const [currentMuseum, setCurrentMuseum] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch all museums
  const fetchMuseums = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const fetchedMuseums = await getAllMuseums();
      setMuseums(fetchedMuseums);
      return fetchedMuseums;
    } catch (err) {
      console.error('Failed to fetch museums:', err);
      setError(err.message);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch a single museum by ID
  const fetchMuseum = useCallback(async (museumId) => {
    setIsLoading(true);
    setError(null);

    try {
      const museum = await getMuseum(museumId);
      setCurrentMuseum(museum);
      return museum;
    } catch (err) {
      console.error('Failed to fetch museum:', err);
      setError(err.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Create a new museum
  const handleCreateMuseum = useCallback(async (museumData) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await createMuseum(museumData);

      // Refresh museums list
      const updatedMuseums = await getAllMuseums();
      setMuseums(updatedMuseums);

      // Set the new museum as current
      const newMuseum = await getMuseum(result.id);
      setCurrentMuseum(newMuseum);

      return result;
    } catch (err) {
      console.error('Failed to create museum:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update a museum
  const handleUpdateMuseum = useCallback(async (museumId, updates) => {
    setIsLoading(true);
    setError(null);

    try {
      await updateMuseum(museumId, updates);

      // Refresh the museum data
      const updated = await getMuseum(museumId);
      setCurrentMuseum(updated);

      // Update in the list
      setMuseums(prev =>
        prev.map(m => m.id === museumId ? updated : m)
      );

      return { success: true };
    } catch (err) {
      console.error('Failed to update museum:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Delete a museum
  const handleDeleteMuseum = useCallback(async (museumId) => {
    setIsLoading(true);
    setError(null);

    try {
      await deleteMuseum(museumId);

      // Remove from list
      setMuseums(prev => prev.filter(m => m.id !== museumId));

      // Clear current if it was the deleted one
      if (currentMuseum?.id === museumId) {
        setCurrentMuseum(null);
      }

      return { success: true };
    } catch (err) {
      console.error('Failed to delete museum:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentMuseum]);

  // Room management
  const handleAddRoom = useCallback(async (museumId, room) => {
    setError(null);

    try {
      const result = await addRoom(museumId, room);

      // Refresh the museum data
      const updated = await getMuseum(museumId);
      setCurrentMuseum(updated);

      return result;
    } catch (err) {
      console.error('Failed to add room:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  const handleRemoveRoom = useCallback(async (museumId, room) => {
    setError(null);

    try {
      await removeRoom(museumId, room);

      // Refresh the museum data
      const updated = await getMuseum(museumId);
      setCurrentMuseum(updated);

      return { success: true };
    } catch (err) {
      console.error('Failed to remove room:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  const handleUpdateRooms = useCallback(async (museumId, rooms) => {
    setError(null);

    try {
      await updateRooms(museumId, rooms);

      // Refresh the museum data
      const updated = await getMuseum(museumId);
      setCurrentMuseum(updated);

      return { success: true };
    } catch (err) {
      console.error('Failed to update rooms:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Category management
  const handleAddCategory = useCallback(async (museumId, category) => {
    setError(null);

    try {
      await addCategory(museumId, category);

      // Refresh the museum data
      const updated = await getMuseum(museumId);
      setCurrentMuseum(updated);

      return { success: true };
    } catch (err) {
      console.error('Failed to add category:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  const handleRemoveCategory = useCallback(async (museumId, category) => {
    setError(null);

    try {
      await removeCategory(museumId, category);

      // Refresh the museum data
      const updated = await getMuseum(museumId);
      setCurrentMuseum(updated);

      return { success: true };
    } catch (err) {
      console.error('Failed to remove category:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  const handleUpdateCategories = useCallback(async (museumId, categories) => {
    setError(null);

    try {
      await updateCategories(museumId, categories);

      // Refresh the museum data
      const updated = await getMuseum(museumId);
      setCurrentMuseum(updated);

      return { success: true };
    } catch (err) {
      console.error('Failed to update categories:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Select a museum (from already fetched list)
  const selectMuseum = useCallback((museumId) => {
    const museum = museums.find(m => m.id === museumId);
    setCurrentMuseum(museum || null);
    return museum;
  }, [museums]);

  // Guide linking management
  const handleLinkGuide = useCallback(async (museumId, guideId) => {
    setError(null);

    try {
      await linkGuide(museumId, guideId);

      // Refresh the museum data
      const updated = await getMuseum(museumId);
      setCurrentMuseum(updated);

      // Update in the list
      setMuseums(prev =>
        prev.map(m => m.id === museumId ? updated : m)
      );

      return { success: true };
    } catch (err) {
      console.error('Failed to link guide:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  const handleUnlinkGuide = useCallback(async (museumId, guideId) => {
    setError(null);

    try {
      await unlinkGuide(museumId, guideId);

      // Refresh the museum data
      const updated = await getMuseum(museumId);
      setCurrentMuseum(updated);

      // Update in the list
      setMuseums(prev =>
        prev.map(m => m.id === museumId ? updated : m)
      );

      return { success: true };
    } catch (err) {
      console.error('Failed to unlink guide:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  const handleUpdateLinkedGuides = useCallback(async (museumId, guideIds) => {
    setError(null);

    try {
      await updateLinkedGuides(museumId, guideIds);

      // Refresh the museum data
      const updated = await getMuseum(museumId);
      setCurrentMuseum(updated);

      // Update in the list
      setMuseums(prev =>
        prev.map(m => m.id === museumId ? updated : m)
      );

      return { success: true };
    } catch (err) {
      console.error('Failed to update linked guides:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Find museum by guide ID
  const findMuseumByGuideId = useCallback(async (guideId) => {
    try {
      return await getMuseumByGuideId(guideId);
    } catch (err) {
      console.error('Failed to find museum by guide ID:', err);
      return null;
    }
  }, []);

  return {
    // State
    museums,
    currentMuseum,
    isLoading,
    error,

    // Actions
    fetchMuseums,
    fetchMuseum,
    createMuseum: handleCreateMuseum,
    updateMuseum: handleUpdateMuseum,
    deleteMuseum: handleDeleteMuseum,
    selectMuseum,
    setCurrentMuseum,
    clearError,

    // Room management
    addRoom: handleAddRoom,
    removeRoom: handleRemoveRoom,
    updateRooms: handleUpdateRooms,

    // Category management
    addCategory: handleAddCategory,
    removeCategory: handleRemoveCategory,
    updateCategories: handleUpdateCategories,

    // Guide linking management
    linkGuide: handleLinkGuide,
    unlinkGuide: handleUnlinkGuide,
    updateLinkedGuides: handleUpdateLinkedGuides,
    findMuseumByGuideId
  };
};

export default useMuseums;
